// Kinhall (#/kinhall, rules in shared/kinhall.ts): the home hall where the
// roads of the three realms meet. Your trainer and everyone else here walk its
// floor in their own looks with their companions; a World chat line pops up
// over its speaker. Stations stand around Urd's Well, which heals the team.
// The hunt keeps running on the server the whole time (the live strip shows
// it), so nothing here pauses anything.
//
// The scene is three painted layers (art/rig3d/build-kinhall.ts: public/art/
// kinhall-floor, -back and -front), with the stations and lanterns as props
// (prop-hall-*) sorted among the trainers. The art thread owns the drawing.
import { HALL_W, HALL_H, FLOOR, SPAWN, STATIONS, STATION, WELL, SQUASH, DISPLAYS, EMOTE, EMOTE_MS, EMOTE_GAP_MS, onFloor, insideBlocker, ownsEmote, emoteBar as barEmotes, type StationId, type DisplayId } from '../shared/kinhall.ts';
import { ALTAR } from '../shared/altar.ts';
import { SPECIES } from '../shared/species.ts';
import { unlocked } from '../shared/unlocks.ts';
import { EGG_UNLOCK_LEVEL } from '../shared/eggs.ts';
import { TOUR_TIERS, isTier } from '../shared/tournament.ts';
import { RAMPS } from '../art/palette.ts';
import type { ClientMsg, CompanionRef } from '../shared/protocol.ts';
import { applyHall, type HallPatch, type HallTrainer } from '../shared/kinhall-patch.ts';
import { S, serverNow } from './state.ts';
import { chatShown } from './chatfilter.ts';
import { canvasDpr, frameTime, onLite } from './perf.ts';
import { sprite, has, sheet, drawable as ready, sheetInfo, sheetCell, artInfo, iconUrl, spriteUrl, uiIcon } from './assets.ts';
import { confirmClick, isArmed } from './confirm.ts';
import { heading, steer, holdDir, holdFace, type Dir, type Heading } from './facing.ts';
import { mySprite, trainerSprite } from './trainerart.ts';
import { myArt, artOf } from './skins.ts';
import { esc } from './fmt.ts';
import { go } from './router.ts';
import { toast } from './dialog.ts';
import { questsWaiting } from './quests.ts';
import { altar } from './creatures.ts';
import { patch } from './dom.ts';
import { HALL_LANTERNS, HALL_DECOR } from './kinhall-art.ts';
import { viewCreature } from './peek.ts';

const TILE = 24;
const SW = HALL_W * TILE, SH = HALL_H * TILE;
const WALK_TPS = 3.4;
const SHEET_SCALE = 0.75, COMP_SCALE = 0.62;
const WALK_MS = 140, IDLE_MS = 150;
const R = RAMPS;

type Facing = 'front' | 'left' | 'back' | 'right';
const ROW: Record<Facing, number> = { front: 0, left: 1, back: 2, right: 3 };
const FACING: Record<Dir, Facing> = { down: 'front', up: 'back', left: 'left', right: 'right' };

interface Walker {
  name: string; worn: string; lv: number; tag?: string; comp?: CompanionRef;
  x: number; y: number; tx: number; ty: number;
  dir: Dir; head: Heading; moved: number; seed: number;
  /** The companion trots behind along this point. */
  cx: number; cy: number; cface: Facing; chead: Heading; cmoved: number;
  bubble?: { text: string; until: number };
  emote?: { id: string; at: number };
  seen: number;
}

let send: (m: ClientMsg) => void = () => {};
let host: HTMLElement;
let canvas: HTMLCanvasElement;
let g: CanvasRenderingContext2D;
let plates: HTMLElement;
let card: HTMLElement;
let bar: HTMLElement;
let dpr = 1, k = 2;
/** The view's top-left in map px. It follows you; a drag pans it away (`pan`, map px) until you walk again. */
const cam = { x: 0, y: 0, ok: false };
const pan = { x: 0, y: 0 };
/** A press on the scene: a drag once it has moved a few px, else a click that walks. */
let press: { id: number; x: number; y: number; lx: number; ly: number; drag: boolean } | null = null;
let dragged = 0;
const DRAG_PX = 6;
let looping = false;
let lastT = 0;

/** Others in the hall, by name; `me` is drawn from local input so it never lags. */
const others = new Map<string, Walker>();
/** The server's roster as last heard (shared/kinhall-patch.ts), everyone included. */
const roster = new Map<string, HallTrainer>();
let me: Walker | null = null;
/** The Hall of Legends plinths (server/kinhall.ts HallDisplay). */
interface Display { who: string; species?: string; aura?: boolean; skin?: string; outfit?: string; comp?: CompanionRef; lv?: number; value?: number; tier?: string }
let displays: Partial<Record<DisplayId, Display>> = {};
/** Whether the server's roster has arrived; until then the plinths say nothing rather than "Unclaimed". */
let hallHeard = false;
/** Movement keys held (WASD and arrows). */
const keys = new Set<string>();
let keySentAt = 0;
let entered = false;
let lastBc = 0;
/** A station clicked: walk there, then use it. */
let heading_to: StationId | null = null;
let healFx = -1e9;

const walker = (name: string, worn: string, x: number, y: number): Walker => ({
  name, worn, lv: 1, x, y, tx: x, ty: y, dir: 'down', head: heading(), moved: -1e9, seed: Math.random() * 5000,
  cx: x - 0.6, cy: y - 0.5, cface: 'front', chead: heading(), cmoved: -1e9, seen: performance.now(),
});

/** Opens Kinhall (the tutorial calls this to start a new trainer here). */
export function openKinhall() { go('#/kinhall'); }

export function mountKinhall(el: HTMLElement, s: (m: ClientMsg) => void) {
  send = s; host = el;
  host.innerHTML = '<canvas class="hallfield" aria-label="Kinhall"></canvas><div class="hallplates"></div><div class="hallbar"><div class="hallcard"></div><div class="hallemotes" role="toolbar" aria-label="Emotes"></div></div>';
  canvas = host.querySelector('canvas')!;
  plates = host.querySelector('.hallplates')!;
  card = host.querySelector('.hallcard')!;
  bar = host.querySelector('.hallbar')!;
  g = canvas.getContext('2d')!;
  const fit = () => {
    dpr = canvasDpr();
    const r = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(r.width * dpr));
    canvas.height = Math.max(1, Math.round(r.height * dpr));
  };
  new ResizeObserver(fit).observe(canvas);
  onLite(fit);
  canvas.addEventListener('click', onCanvasClick);
  // Drag the scene to look around (a finger on a phone, the mouse anywhere); walking brings the view back to you.
  canvas.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || press) return;
    press = { id: e.pointerId, x: e.clientX, y: e.clientY, lx: e.clientX, ly: e.clientY, drag: false };
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!press || e.pointerId !== press.id) return;
    if (!press.drag && Math.hypot(e.clientX - press.x, e.clientY - press.y) < DRAG_PX) return;
    if (!press.drag) { press.drag = true; canvas.setPointerCapture(e.pointerId); canvas.classList.add('panning'); }
    pan.x -= ((e.clientX - press.lx) * dpr) / k; pan.y -= ((e.clientY - press.ly) * dpr) / k;
    press.lx = e.clientX; press.ly = e.clientY;
  });
  const release = (e: PointerEvent) => {
    if (!press || e.pointerId !== press.id) return;
    if (press.drag) dragged = performance.now();
    press = null; canvas.classList.remove('panning');
  };
  canvas.addEventListener('pointerup', release);
  canvas.addEventListener('pointercancel', release);
  plates.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-hall]');
    if (b) useStation(b.dataset.hall as StationId);
  });
  emoteBar = host.querySelector('.hallemotes')!;
  emoteBar.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-emote]');
    if (b) playEmote(b.dataset.emote!);
  });
  // WASD or the arrows walk; number keys play the emotes in bar order. Only while Kinhall is open.
  document.addEventListener('keydown', (e) => {
    if (host.hidden || e.ctrlKey || e.metaKey || e.altKey || (e.target as HTMLElement).closest?.('input, textarea, select, [contenteditable]')) return;
    const key = MOVE_KEYS[e.key.toLowerCase()];
    if (key) { keys.add(key); heading_to = null; follow(); e.preventDefault(); return; }
    const n = Number(e.key);
    const bar = S.trainer ? barEmotes(S.trainer) : [];
    if (n >= 1 && n <= bar.length) playEmote(bar[n - 1].id);
  });
  document.addEventListener('keyup', (e) => { const key = MOVE_KEYS[e.key.toLowerCase()]; if (key) keys.delete(key); });
  window.addEventListener('blur', () => keys.clear());
}

/** The socket came back: the server forgot us, so arrive again. */
export function kinhallReconnected() { entered = false; }

/** Someone here played an emote. */
export function onKinhallEmote(m: { n: string; id: string }) {
  if (m.n === S.trainer?.name) return;
  const w = others.get(m.n);
  if (w && EMOTE[m.id]) w.emote = { id: m.id, at: performance.now() };
}

let emoteBar: HTMLElement;
let lastEmote = 0;
/** Play an emote you own; one you don't is bought on a second click (diamonds), then played. */
function playEmote(id: string) {
  const t = S.trainer, e = EMOTE[id];
  if (!t || !e || !me) return;
  if (!ownsEmote(t, id)) {
    if (t.diamonds < e.price) { toast(`${e.name} costs ${e.price} diamonds`, 'err'); return; }
    if (confirmClick(`emote:${id}`)) send({ t: 'emoteBuy', id });
    renderKinhall();
    return;
  }
  const now = performance.now();
  if (now - lastEmote < EMOTE_GAP_MS) return;
  lastEmote = now;
  me.emote = { id, at: now };
  send({ t: 'kinhall', op: 'emote', id });
}

/** The server's roster of who stands here: the whole of it (list), or what changed since the last (shared/kinhall-patch.ts). */
export function onKinhall(m: { list?: HallTrainer[]; displays?: Partial<Record<DisplayId, Display>> } & HallPatch) {
  // The plinths come with every whole roster, and with a patch only when they changed.
  if (m.list || m.displays) {
    displays = {}; hallHeard = true;
    for (const id of DISPLAYS) { const d = m.displays?.[id]; if (d && (!d.species || SPECIES[d.species])) displays[id] = d; }
  }
  applyHall(roster, m);
  const mine = S.trainer?.name;
  for (const r of roster.values()) {
    // A stand-in name (server/auth.ts NAME_PENDING) never shows; the server leaves those trainers out too.
    if (r.n === mine || r.n.startsWith('_new')) continue;
    let w = others.get(r.n);
    if (!w) { w = walker(r.n, r.o, r.x, r.y); others.set(r.n, w); }
    w.worn = r.o; w.lv = r.lv; w.tag = r.tag; w.comp = r.comp; w.tx = r.x; w.ty = r.y;
  }
  for (const n of [...others.keys()]) if (!roster.has(n) || n === mine) others.delete(n);
  if (S.page === 'kinhall') renderKinhall();
}

/** Called on every render: arrive when the page opens, leave when it closes. */
export function kinhallPresence(open: boolean) {
  if (!host) return;
  if (host.hidden === open) host.hidden = !open;
  if (open && !entered && S.online) {
    entered = true;
    send({ t: 'kinhall', op: 'enter' });
    lastBc = S.broadcasts.length ? S.broadcasts[S.broadcasts.length - 1].ts : 0;
    if (!me) { const x = SPAWN.x + (Math.random() * 2 - 1) * SPAWN.spread; me = walker(S.trainer?.name ?? '', '', x, SPAWN.y); }
    cam.ok = false;
  } else if (!open && entered) {
    entered = false; heading_to = null;
    send({ t: 'kinhall', op: 'leave' });
    others.clear(); roster.clear();
  }
  if (open && !looping) { looping = true; lastT = performance.now(); requestAnimationFrame(frame); }
}

// ---------------- stations ----------------
interface PlateState { locked?: string; sub?: string; dot?: boolean; ready?: boolean; hidden?: boolean }

function stationState(id: StationId): PlateState {
  const t = S.trainer!, now = serverNow();
  switch (id) {
    case 'well': return { ready: true };
    case 'altar': return t.level < ALTAR.minLevel ? { locked: `Lv ${ALTAR.minLevel}` } : {};
    case 'hatchery': {
      const eggs = t.eggs?.length ?? 0;
      if (!eggs && t.level < EGG_UNLOCK_LEVEL) return { locked: `Lv ${EGG_UNLOCK_LEVEL}` };
      return eggs ? { sub: `${eggs} warming`, dot: t.eggs!.some((e) => e.hatchAt <= now) } : {};
    }
    case 'quests': {
      const today = new Date().toISOString().slice(0, 10);
      return { dot: t.daily.last !== today || questsWaiting().count > 0 };
    }
    case 'aura': case 'best': case 'tour': case 'top': case 'arena': {
      const d = displays[id];
      if (!d) return { sub: hallHeard ? 'Unclaimed' : '…' };
      const what = id === 'aura' || id === 'best' ? `${d.aura ? 'Aura ' : ''}${SPECIES[d.species!].name} · ` : id === 'top' && d.lv ? `Lv ${d.lv.toLocaleString('en-US')} · `
        : id === 'tour' && isTier(d.tier) ? `${TOUR_TIERS[d.tier].name} · ` : '';
      return { sub: `${what}${d.who}` };
    }
    default: return {};
  }
}

function useStation(id: StationId) {
  const st = stationState(id);
  if (st.locked) { toast(`${STATION[id].name} opens at trainer ${st.locked}`); return; }
  if (!me) return;
  const s = STATION[id];
  if (Math.hypot(me.x - s.stand.x, me.y - s.stand.y) > 0.4) {
    heading_to = id;
    walkTo(s.stand.x, s.stand.y);
    return;
  }
  arrive(id);
}

function arrive(id: StationId) {
  heading_to = null;
  const t = S.trainer!;
  switch (id) {
    case 'well':
      healFx = performance.now();
      if (me) me.dir = 'up';
      return send({ t: 'kinhallHeal' });
    case 'vault': return go('#/creatures');
    case 'altar': go('#/creatures'); void altar(); return;
    case 'aura': case 'best': case 'tour': case 'top': case 'arena': {
      const d = displays[id];
      // The creature plinths open the creature on them (read-only); the trainer plinths open the trainer.
      if (d && id === 'aura') return viewCreature('legend');
      if (d && id === 'best') return viewCreature('best', d.who);
      if (d) return go(`#/player/${encodeURIComponent(d.who)}`);
      return go(id === 'top' ? '#/leaderboards/level' : id === 'arena' ? '#/leaderboards/arena' : id === 'tour' ? '#/tournaments' : '#/records');
    }
    case 'hatchery': return go('#/shop/eggs');
    case 'merchant': return go('#/shop');
    case 'board': return go('#/market');
    // The daily reward is claimed on the Quests page or the gift menu, never on the way past the board.
    case 'quests': return go('#/quests');
    // Out through the Road gate: a hunt stopped in Kinhall (TrainerDTO.stopped) resumes.
    case 'gate': if (t.stopped) send({ t: 'ground', id: t.ground }); return go('#/hunt');
  }
}

/** Back to following you: the view eases home from wherever it was dragged. */
function follow() { pan.x = 0; pan.y = 0; }

function walkTo(x: number, y: number) {
  if (!me) return;
  follow();
  const p = onFloor(x, y);
  me.tx = p.x; me.ty = p.y;
  send({ t: 'kinhall', op: 'walk', x: p.x, y: p.y });
}

function onCanvasClick(e: MouseEvent) {
  // The end of a drag is not a click.
  if (performance.now() - dragged < 250) return;
  const r = canvas.getBoundingClientRect();
  const wx = (((e.clientX - r.left) * dpr) / k + cam.x) / TILE, wy = (((e.clientY - r.top) * dpr) / k + cam.y) / TILE;
  // A click on a station's prop uses it.
  for (const s of STATIONS) if (Math.abs(wx - s.x) < 1.3 && wy < s.y + 0.5 && wy > s.y - 2.4) { useStation(s.id); return; }
  heading_to = null;
  walkTo(wx, wy);
}

/** The station plates over the scene: what each is and whether it wants you. */
export function renderKinhall() {
  if (!host || host.hidden || !S.trainer) return;
  const n = others.size + 1;
  patch(card, `${n === 1 ? 'Just you' : `${n} trainers here`}`);
  patch(plates, STATIONS.map((s) => {
    const st = stationState(s.id);
    const cls = `hplate${st.locked ? ' locked' : ''}${st.ready ? ' ready' : ''}${s.id === 'gate' ? ' gate' : ''}`;
    const sub = st.locked ?? st.sub ?? '';
    return `<button type="button" class="${cls}" data-hall="${s.id}" data-key="${s.id}"><span class="nm">${esc(s.name)}</span>${sub ? `<span class="sub">${esc(sub)}</span>` : ''}${st.dot ? '<i class="dot"></i>' : ''}</button>`;
  }).join(''));
  // A changed plate loses its placement to the patch: place them all again.
  plateKey = '';
  placePlates();
  patch(emoteBar, barEmotes(S.trainer!).map((e, i) => {
    const mine = ownsEmote(S.trainer!, e.id), armed = isArmed(`emote:${e.id}`);
    const sub = mine ? '' : armed ? `<span class="sub">Buy</span>` : `<span class="sub"><img src="${iconUrl('diamond')}" alt="Diamonds">${e.price}</span>`;
    return `<button type="button" class="hemote${mine ? '' : ' locked'}${armed ? ' armed' : ''}" data-emote="${e.id}" data-key="${e.id}" title="${esc(e.name)} (${i + 1})">${emoteImg(e.id)}<span class="nm">${esc(e.name)}</span>${sub}</button>`;
  }).join(''));
}

let plateKey = '';
interface Box { l: number; t: number; r: number; b: number }
const hit = (a: Box, b: Box) => a.l < b.r && b.l < a.r && a.t < b.b && b.t < a.b;
/**
 * Each plate over its station. One whose spot is above the view while its station shows hangs under it instead.
 * A plate in view that would sit under the bottom bar rises above it. A station out of view keeps its plate at the
 * edge, pointing the way (nearest stations first); edge plates slide along their edge to make room for each other.
 */
function placePlates() {
  const key = `${cam.x.toFixed(1)}:${cam.y.toFixed(1)}:${k}:${canvas.width}:${canvas.height}`;
  if (key === plateKey && plates.childElementCount) return;
  plateKey = key;
  const W = canvas.width / dpr, H = canvas.height / dpr;
  const o = host.getBoundingClientRect(), br = bar.getBoundingClientRect();
  const barBox: Box = { l: br.left - o.left - 4, t: br.top - o.top - 4, r: br.right - o.left + 4, b: br.bottom - o.top + 4 };
  const taken: Box[] = [barBox];
  const edges: { el: HTMLElement; x: number; y: number; half: number; tall: number; dir: string; d: number }[] = [];
  const toX = (x: number) => ((x * TILE - cam.x) * k) / dpr, toY = (y: number) => ((y * TILE - cam.y) * k) / dpr;
  // The Hall of Legends labels hang in one row, or in two staggered rows when neighbours would touch (a narrow view, long names).
  for (const el of plates.children as HTMLCollectionOf<HTMLElement>) el.classList.remove('edge');
  const hall = DISPLAYS.map((id) => plates.querySelector<HTMLElement>(`[data-hall="${id}"]`));
  const stagger = hall.some((el, i) => {
    const next = hall[i + 1];
    return !!el && !!next && toX(STATION[DISPLAYS[i + 1]].x) - toX(STATION[DISPLAYS[i]].x) < (el.offsetWidth + next.offsetWidth) / 2 + 8;
  });
  for (const el of plates.children as HTMLCollectionOf<HTMLElement>) {
    const s = STATION[el.dataset.hall as StationId];
    el.classList.remove('edge');
    el.style.visibility = '';
    // A Hall of Legends plate hangs under its plinth, like a museum label; the rest sit above their station.
    const below = DISPLAYS.includes(s.id as DisplayId);
    const half = el.offsetWidth / 2 + 6, tall = el.offsetHeight + 6;
    const x = toX(s.x), foot = toY(s.y);
    const row = below && stagger ? DISPLAYS.indexOf(s.id as DisplayId) % 2 : 0;
    let y = toY(s.y - (PLATE_LIFT[s.id] ?? 2.4)) + (below ? tall * (1 + row) : 0);
    // Its spot is off the top but the station stands in view: hang it under the station.
    if (y < tall && foot > 0 && foot < H && x > 0 && x < W) y = foot + tall + 8;
    // In view but under the bottom bar: rise above the bar.
    if (y > barBox.t && y - tall < barBox.b && x + half > barBox.l && x - half < barBox.r && foot < H + tall) y = barBox.t;
    const cx = Math.min(W - half, Math.max(half, x)), cy = Math.min(H - 6, Math.max(tall, y));
    if (cx === x && cy === y) {
      el.style.transform = `translate(${Math.round(cx)}px, ${Math.round(cy)}px) translate(-50%, -100%)`;
      taken.push({ l: cx - half, t: cy - tall, r: cx + half, b: cy });
      continue;
    }
    const dir = Math.abs(cx - x) >= Math.abs(cy - y) ? (x < cx ? 'w' : 'e') : y < cy ? 'n' : 's';
    edges.push({ el, x: cx, y: dir === 's' ? Math.min(cy, barBox.t) : cy, half, tall, dir, d: me ? Math.hypot(me.x - s.x, me.y - s.y) : 0 });
  }
  edges.sort((a, b) => a.d - b.d);
  for (const e of edges) {
    e.el.classList.add('edge');
    e.el.dataset.dir = e.dir;
    // An edge plate is more compact: measure it again.
    e.half = e.el.offsetWidth / 2 + 6; e.tall = e.el.offsetHeight + 6;
    e.x = Math.min(W - e.half, Math.max(e.half, e.x));
    // Slide along the edge, nearest free slot first: down and up the side for east and west, along the top or bottom for north and south.
    const side = e.dir === 'w' || e.dir === 'e';
    const stepPx = side ? e.tall : e.half * 2;
    let placed: Box | null = null, px = e.x, py = e.y;
    for (let i = 0; i < 24 && !placed; i++) {
      const off = (i % 2 ? -1 : 1) * Math.ceil(i / 2) * stepPx;
      const nx = side ? e.x : Math.min(W - e.half, Math.max(e.half, e.x + off));
      const ny = side ? Math.min(H - 6, Math.max(e.tall, e.y + off)) : e.y;
      const b = { l: nx - e.half, t: ny - e.tall, r: nx + e.half, b: ny };
      if (!taken.some((t) => hit(t, b))) { placed = b; px = nx; py = ny; }
    }
    if (!placed) { e.el.style.visibility = 'hidden'; continue; }
    taken.push(placed);
    e.el.style.transform = `translate(${Math.round(px)}px, ${Math.round(py)}px) translate(-50%, -100%)`;
  }
}
const MOVE_KEYS: Record<string, string> = { w: 'up', arrowup: 'up', s: 'down', arrowdown: 'down', a: 'left', arrowleft: 'left', d: 'right', arrowright: 'right' };
/** Tiles above a station's foot point where its plate sits. */
const PLATE_LIFT: Partial<Record<StationId, number>> = { well: 2.1, aura: -0.2, best: -0.2, tour: -0.2, top: -0.2, arena: -0.2, vault: 1.9, altar: 1.7, hatchery: 1.3, merchant: 2.9, board: 2.3, quests: 2.3, gate: 0.4 };

// ---------------- movement ----------------
let keying = false;
/** Held movement keys walk you: the target stays a step ahead, and the server hears where you are a few times a second. */
function keyWalk(w: Walker, now: number) {
  const dx = (keys.has('right') ? 1 : 0) - (keys.has('left') ? 1 : 0), dy = (keys.has('down') ? 1 : 0) - (keys.has('up') ? 1 : 0);
  if (dx || dy) {
    const d = Math.hypot(dx, dy), p = onFloor(w.x + (dx / d) * 0.6, w.y + (dy / d) * 0.6);
    w.tx = p.x; w.ty = p.y; keying = true;
    if (now - keySentAt > 150) { keySentAt = now; send({ t: 'kinhall', op: 'walk', x: p.x, y: p.y }); }
  } else if (keying) {
    keying = false; w.tx = w.x; w.ty = w.y;
    const p = onFloor(w.x, w.y);
    // The server takes one spot per 120 ms: send the stop a beat after the last step.
    setTimeout(() => send({ t: 'kinhall', op: 'walk', x: p.x, y: p.y }), Math.max(0, 130 - (now - keySentAt)));
    keySentAt = now;
  }
}
function step(w: Walker, dtMs: number, now: number) {
  const dx = w.tx - w.x, dy = w.ty - w.y, d = Math.hypot(dx, dy);
  if (d > 0.03) {
    // Far behind (a tab that slept, a long hop): catch up quicker.
    const v = WALK_TPS * (d > 6 ? 3 : 1) * dtMs / 1000;
    const s = Math.min(d, v);
    let nx = w.x + (dx / d) * s, ny = w.y + (dy / d) * s;
    // Pushed back to the rim of the Well or a station's prop: walking into one slides along it.
    const out = insideBlocker(nx, ny);
    if (out) { nx = out.x; ny = out.y; }
    const mx = nx - w.x, my = ny - w.y;
    w.x = nx; w.y = ny;
    w.moved = now;
    steer(w.head, mx, my, dtMs);
    w.dir = holdDir(w.dir, w.head.vx, w.head.vy);
    // Stuck against the rim with the target inside it: stop here.
    if (Math.hypot(mx, my) < s * 0.2) { w.tx = w.x; w.ty = w.y; }
  }
  // The companion keeps about a tile behind and to the side.
  const back = { x: w.x - (w.dir === 'left' ? -0.9 : w.dir === 'right' ? 0.9 : 0.7), y: w.y - (w.dir === 'up' ? -0.6 : w.dir === 'down' ? 0.5 : 0.15) };
  const cdx = back.x - w.cx, cdy = back.y - w.cy, cd = Math.hypot(cdx, cdy);
  if (cd > 0.25) {
    const s = Math.min(cd - 0.2, WALK_TPS * 1.1 * dtMs / 1000 * (cd > 3 ? 3 : 1));
    w.cx += (cdx / cd) * s; w.cy += (cdy / cd) * s;
    w.cmoved = now;
    steer(w.chead, cdx, cdy, dtMs);
    w.cface = holdFace(w.cface, w.chead.vx, w.chead.vy);
  } else if (now - w.cmoved > 400) {
    // Settled: face the same way as its trainer.
    w.cface = FACING[w.dir];
  }
}

function bubbles(now: number) {
  const b = S.broadcasts;
  if (!b.length || b[b.length - 1].ts <= lastBc) return;
  for (let i = b.length - 1; i >= 0 && b[i].ts > lastBc; i--) {
    const line = b[i];
    if (line.kind !== 'chat' || (line.ch ?? 'world') !== 'world' || !line.who || line.creature) continue;
    const w = line.who === S.trainer?.name ? me : others.get(line.who);
    if (w && !w.bubble) w.bubble = { text: bubbleText(line.text), until: now + 6000 };
  }
  lastBc = b[b.length - 1].ts;
}

// ---------------- drawing ----------------
const sx = (x: number) => Math.round((x * TILE - cam.x) * k);
const sy = (y: number) => Math.round((y * TILE - cam.y) * k);

function shadow(x: number, y: number, w: number, a = 0.24) {
  g.globalAlpha = a; g.fillStyle = '#000';
  g.beginPath(); g.ellipse(sx(x), sy(y), w * 0.32 * k, w * 0.1 * k, 0, 0, Math.PI * 2); g.fill();
  g.globalAlpha = 1;
}

/** One frame of a sheet (rows: facings; columns: walk, idle, attack) standing at (x, y), at `scale` map px per sheet px. */
function drawSheet(id: string, aura: boolean, x: number, y: number, face: Facing, moving: boolean, seed: number, t: number, scale = SHEET_SCALE, squash = 1) {
  const info = sheetInfo(id)!;
  const img = sheet(id, aura);
  if (!ready(img)) return;
  const n = info.size, p = sheetCell(info);
  const col = moving ? Math.floor(t / WALK_MS) % info.walk : info.walk + (Math.floor((t + seed) / IDLE_MS) % info.idle);
  const w = n * scale;
  const foot = sy(y + 0.12) + 7 * scale * k;
  g.drawImage(img, col * p, ROW[face] * p, p, p, Math.round(sx(x) - (w / 2) * k), Math.round(foot - w * squash * k), Math.round(w * k), Math.round(w * squash * k));
}

const SPIN: Facing[] = ['front', 'right', 'back', 'left'];
function drawTrainer(w: Walker, fit: string, t: number, now: number) {
  let moving = now - w.moved < 160;
  let face = FACING[w.dir], lift = 0, squash = 1;
  // An emote's motion: a hop, a spin through the facings, a bow.
  const em = w.emote && now - w.emote.at < EMOTE_MS ? EMOTE[w.emote.id] : null;
  if (em) {
    const ph = (now - w.emote!.at) / EMOTE_MS;
    if (em.motion === 'hop') lift = Math.abs(Math.sin(ph * Math.PI * 4)) * 0.32;
    if (em.motion === 'dance') { face = SPIN[Math.floor((now - w.emote!.at) / 200) % 4]; moving = true; lift = Math.abs(Math.sin(ph * Math.PI * 8)) * 0.08; }
    if (em.motion === 'bow' && ph > 0.15 && ph < 0.75) squash = 0.86;
  }
  if (sheetInfo(fit)) { shadow(w.x, w.y + 0.08, 20); drawSheet(fit, false, w.x, w.y + 0.03 - lift, face, moving, w.seed, t, SHEET_SCALE, squash); return; }
  const id = `${fit}-${w.dir}-0`;
  if (has(id)) { const img = sprite(id); if (ready(img)) { shadow(w.x, w.y + 0.1, 20); g.drawImage(img, sx(w.x) - 16 * k, sy(w.y + 0.15) - 32 * k, 32 * k, 32 * k); } }
}

function drawCompanion(w: Walker, comp: { id: string; aura: boolean }, t: number, now: number) {
  if (!sheetInfo(comp.id)) return;
  shadow(w.cx, w.cy + 0.08, 18, 0.2);
  drawSheet(comp.id, comp.aura, w.cx, w.cy, w.cface, now - w.cmoved < 160, w.seed + 900, t, SHEET_SCALE * COMP_SCALE);
}

function label(text: string, x: number, y: number, color = '#f3e6c4', px = 11) {
  g.font = `600 ${Math.round(px * dpr)}px "Crimson Pro", Georgia, serif`;
  g.textAlign = 'center'; g.lineJoin = 'round';
  g.lineWidth = 3 * dpr; g.strokeStyle = 'rgba(20,14,10,.9)'; g.strokeText(text, x, y);
  g.fillStyle = color; g.fillText(text, x, y);
}

function bubble(text: string, x: number, y: number, alpha: number) {
  g.font = `500 ${Math.round(12 * dpr)}px "Crimson Pro", Georgia, serif`;
  const words = text.split(' '), lines: string[] = [];
  let cur = '';
  const max = 150 * dpr;
  for (const wd of words) { const nx = cur ? `${cur} ${wd}` : wd; if (g.measureText(nx).width > max && cur) { lines.push(cur); cur = wd; } else cur = nx; }
  if (cur) lines.push(cur);
  const lh = 14 * dpr, pad = 6 * dpr;
  const bw = Math.max(...lines.map((l) => g.measureText(l).width)) + pad * 2, bh = lines.length * lh + pad * 1.4;
  const bx = Math.round(x - bw / 2), by = Math.round(y - bh - 6 * dpr);
  g.globalAlpha = alpha;
  g.fillStyle = 'rgba(244,232,204,.96)'; g.strokeStyle = '#7a5a2e'; g.lineWidth = Math.max(1, dpr);
  g.beginPath(); g.roundRect(bx, by, bw, bh, 4 * dpr); g.fill(); g.stroke();
  g.beginPath(); g.moveTo(x - 4 * dpr, by + bh); g.lineTo(x, by + bh + 5 * dpr); g.lineTo(x + 4 * dpr, by + bh); g.fill();
  g.fillStyle = '#2a1d12'; g.textAlign = 'center';
  lines.forEach((l, i) => g.fillText(l, x, by + pad + lh * (i + 0.75)));
  g.globalAlpha = 1;
}

/** A rig prop (art/rig3d/props) standing on its foot at tile (x, y). */
function prop(id: string, x: number, y: number, t: number, flip = false, scale = 1) {
  const a = artInfo(id);
  if (!a) return;
  const img = sprite(id);
  if (!ready(img)) return;
  const fr = a.frames > 1 ? Math.floor((t / 260 + x * 3) % a.frames) : 0;
  const w = a.w * scale, h = a.h * scale;
  const px = x * TILE - w / 2, py = y * TILE - (a.h - a.foot) * scale;
  const X = Math.round((px - cam.x) * k), Y = Math.round((py - cam.y) * k);
  if (flip) { g.save(); g.translate(X + w * k, Y); g.scale(-1, 1); g.drawImage(img, fr * a.w, 0, a.w, a.h, 0, 0, w * k, h * k); g.restore(); }
  else g.drawImage(img, fr * a.w, 0, a.w, a.h, X, Y, Math.round(w * k), Math.round(h * k));
}

/** Props standing on the floor among the trainers: the stone lanterns, flickering. */
const PROPS: [string, number, number, boolean?][] = [
  ...HALL_LANTERNS.map(([x, y]): [string, number, number] => ['prop-yomi-lantern', x, y]),
  ...HALL_DECOR,
];

/** The scene's painted layers: the floor (with the sky and the World Tree), what stands behind everyone, what stands in front. */
const LAYERS = ['floor', 'back', 'front'] as const;
let layers: Record<(typeof LAYERS)[number], HTMLImageElement> | null = null;
function drawLayer(img: HTMLImageElement) {
  if (ready(img)) g.drawImage(img, 0, 0, SW, SH, sx(0), sy(0), Math.round(SW * k), Math.round(SH * k));
}
const hash = (x: number, y: number, s = 0) => { let h = (x * 374761393 + y * 668265263 + s * 2147483647) | 0; h = (h ^ (h >>> 13)) * 1274126177; return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };

/** Urd's Well's glow on the floor, pulsing, brighter just after a heal. The basin is a prop (drawStation). */
function drawWell(now: number) {
  const cx = sx(WELL.x), cy = sy(WELL.y), rx = WELL.r * TILE * k, ry = rx / SQUASH;
  const heal = Math.max(0, 1 - (now - healFx) / 1600);
  const glow = g.createRadialGradient(cx, cy, 0, cx, cy, rx * 2.2);
  glow.addColorStop(0, `rgba(159,224,242,${0.18 + heal * 0.4})`); glow.addColorStop(1, 'rgba(159,224,242,0)');
  g.fillStyle = glow; g.fillRect(cx - rx * 2.2, cy - ry * 3.2, rx * 4.4, ry * 5.6);
}

/** Motes rising from the Well (drawn over everything), a burst after a heal. */
function drawMotes(t: number, now: number) {
  const heal = Math.max(0, 1 - (now - healFx) / 1600);
  const n = 10 + Math.round(heal * 26);
  for (let i = 0; i < n; i++) {
    const life = 2600, ph = ((t + hash(i, 7) * life) % life) / life;
    const x = WELL.x + (hash(i, 8) - 0.5) * WELL.r * 1.6, y = WELL.y - ph * (2.2 + heal * 2);
    g.globalAlpha = (1 - ph) * (0.5 + heal * 0.5);
    g.fillStyle = i % 3 ? R.tide[3] : R.light[2];
    g.fillRect(sx(x), sy(y), Math.max(1, Math.round(k * (i % 4 ? 1 : 2))), Math.max(1, Math.round(k * (i % 4 ? 1 : 2))));
  }
  g.globalAlpha = 1;
}

/** Each station's prop (art/rig3d/props/prop-hall-*); the Road gate is part of the front layer. */
const STATION_PROP: Partial<Record<StationId, string>> = {
  well: 'prop-hall-well', vault: 'prop-hall-vault', altar: 'prop-hall-altar', hatchery: 'prop-hall-hatchery', merchant: 'prop-hall-stall',
  board: 'prop-hall-tradeboard', quests: 'prop-hall-questboard', aura: 'prop-hall-plinth', best: 'prop-hall-plinth', tour: 'prop-hall-plinth', top: 'prop-hall-plinth', arena: 'prop-hall-plinth',
};
/** Where a station's prop stands and sorts: its foot point, or the basin's centre for the Well. */
const footY = (id: StationId) => (id === 'well' ? WELL.y : STATION[id].y);

/** A station's prop on its foot point. The Latest Aura plinth glows (its statue is drawn on top by drawStatue). */
function drawStation(id: StationId, t: number) {
  const s = STATION[id], art = STATION_PROP[id];
  if (!art) return;
  if (id === 'aura') {
    const glow = g.createRadialGradient(sx(s.x), sy(s.y - 1.2), 0, sx(s.x), sy(s.y - 1.2), 1.6 * TILE * k);
    glow.addColorStop(0, `rgba(255,214,120,${0.22 + 0.08 * Math.sin(t / 500)})`); glow.addColorStop(1, 'rgba(255,214,120,0)');
    g.fillStyle = glow; g.fillRect(sx(s.x - 1.6), sy(s.y - 2.8), 3.2 * TILE * k, 3.2 * TILE * k);
  }
  prop(art, s.x, footY(id), t);
}

const statues = new Map<string, HTMLCanvasElement>();
/** One sheet frame turned to gilt (keeping its shading), cached by sheet. */
function gilt(id: string): HTMLCanvasElement | null {
  const info = sheetInfo(id), img = sheet(id);
  if (!info || !ready(img)) return null;
  let c = statues.get(id);
  if (c) return c;
  const n = sheetCell(info);
  c = document.createElement('canvas'); c.width = c.height = n;
  const x = c.getContext('2d')!;
  x.drawImage(img, info.walk * n, 0, n, n, 0, 0, n, n);
  x.globalCompositeOperation = 'source-atop';
  const gr = x.createLinearGradient(0, 0, n, n);
  gr.addColorStop(0, '#fff1b8'); gr.addColorStop(0.45, '#e0a73e'); gr.addColorStop(1, '#7a3f1e');
  x.globalAlpha = 0.72; x.fillStyle = gr; x.fillRect(0, 0, n, n);
  statues.set(id, c);
  return c;
}
/** A frame standing on the plinth top at (x, y): in its true colours, idling, or gilt (an unclaimed plinth's placeholder). */
function statueAt(id: string, aura: boolean, real: boolean, x: number, y: number, scale: number, t: number) {
  const info = sheetInfo(id);
  if (!info) return;
  const n = info.size, p = sheetCell(info), w = n * scale;
  const foot = sy(y) + 7 * scale * k;
  const X = Math.round(sx(x) - (w / 2) * k), Y = Math.round(foot - w * k), Wd = Math.round(w * k);
  if (real) {
    const img = sheet(id, aura);
    if (!ready(img)) return;
    const col = info.walk + (Math.floor(t / IDLE_MS) % info.idle);
    g.drawImage(img, col * p, 0, p, p, X, Y, Wd, Wd);
  } else {
    const c = gilt(id);
    if (c) g.drawImage(c, 0, 0, p, p, X, Y, Wd, Wd);
  }
}
/**
 * The Hall of Legends statues, in their true colours: the latest Aura (sparkling), the best catch, and the latest
 * tournament champion, the top trainer and the Arena champion (with their companion, or lead). An unclaimed Aura
 * plinth shows a gilt Phoenix.
 */
function drawStatue(id: DisplayId, t: number) {
  const s = STATION[id], d = displays[id], top = s.y - 17 / TILE;
  if (id === 'aura' || id === 'best') {
    const sp = d?.species ?? (id === 'aura' ? 'phoenix' : null);
    if (!sp) return;
    const art = artOf(sp, d?.skin, !!d?.aura);
    statueAt(art, !!d?.aura, !!d, s.x, top, SHEET_SCALE * 0.85, t);
    if (id === 'aura' && d) for (let i = 0; i < 5; i++) {
      const ph = ((t / 1400) + hash(i, 21)) % 1;
      g.globalAlpha = Math.sin(ph * Math.PI); g.fillStyle = R.gold[3];
      g.fillRect(sx(s.x + (hash(i, 22) - 0.5) * 1.6), sy(top - 0.3 - ph * 1.6), Math.max(1, Math.round(k)), Math.max(1, Math.round(k)));
    }
    g.globalAlpha = 1;
    return;
  }
  if (!d) return;
  const fit = trainerSprite(d.outfit ?? '');
  if (sheetInfo(fit)) statueAt(fit, false, true, s.x - (d.comp ? 0.25 : 0), top, SHEET_SCALE, t);
  if (d.comp) statueAt(artOf(d.comp.species, d.comp.skin, d.comp.aura), !!d.comp.aura, true, s.x + 0.55, top, SHEET_SCALE * COMP_SCALE, t);
}

function frame(t: number) {
  if (!host || host.hidden) { looping = false; return; }
  requestAnimationFrame(frame);
  const now = performance.now();
  const dtMs = Math.min(100, t - lastT); lastT = t;
  frameTime(dtMs);
  const W = canvas.width, H = canvas.height;
  if (W < 2 || H < 2 || !S.trainer) return;
  layers ??= Object.fromEntries(LAYERS.map((n) => { const img = new Image(); img.src = `art/kinhall-${n}.png`; return [n, img]; })) as typeof layers & {};
  g.imageSmoothingEnabled = false;

  // Scale: a view about 40 tiles wide that follows you (13 on a phone held upright), never smaller than the hall's full height.
  k = W < H ? Math.max(H / SH, W / (13 * TILE)) : Math.max(H / SH, Math.min(W / (40 * TILE), H / (22.5 * TILE)));
  if (k >= 2) k = Math.floor(k * 2) / 2;
  const viewW = W / k, viewH = H / k;
  if (me) { me.name = S.trainer.name; keyWalk(me, now); step(me, dtMs, now); }
  for (const w of others.values()) step(w, dtMs, now);
  const focus = me ?? { x: HALL_W / 2, y: HALL_H / 2 };
  const clampX = (v: number) => (viewW >= SW ? (SW - viewW) / 2 : Math.max(0, Math.min(SW - viewW, v)));
  const clampY = (v: number) => (viewH >= SH ? (SH - viewH) / 2 : Math.max(0, Math.min(SH - viewH, v)));
  const baseX = focus.x * TILE - viewW / 2, baseY = focus.y * TILE - viewH / 2;
  const wantX = clampX(baseX + pan.x), wantY = clampY(baseY + pan.y);
  // A drag past the hall's edge goes no further (so dragging back answers at once).
  pan.x = wantX - clampX(baseX); pan.y = wantY - clampY(baseY);
  if (!cam.ok) { cam.x = wantX; cam.y = wantY; cam.ok = true; }
  // Follow with an ease; a drag moves the view under the finger.
  const ease = press?.drag ? 1 : 1 - Math.pow(0.01, dtMs / 1000);
  cam.x += (wantX - cam.x) * ease; cam.y += (wantY - cam.y) * ease;
  cam.x = Math.round(cam.x * k) / k; cam.y = Math.round(cam.y * k) / k;

  g.fillStyle = '#120d0a'; g.fillRect(0, 0, W, H);
  drawLayer(layers.floor);
  drawLayer(layers.back);
  drawWell(now);

  // Everything that stands, back to front.
  const items: { y: number; draw: () => void }[] = [];
  for (const [id, x, y, flip] of PROPS) items.push({ y, draw: () => prop(id, x, y, t, flip) });
  for (const s of STATIONS) if (STATION_PROP[s.id]) items.push({ y: footY(s.id), draw: () => drawStation(s.id, t) });
  for (const id of DISPLAYS) items.push({ y: STATION[id].y + 0.01, draw: () => drawStatue(id, t) });
  const walkers = [...others.values()];
  if (me) walkers.push(me);
  const myComp = (() => {
    const id = S.trainer?.companion ?? S.trainer?.team[0], c = id !== undefined ? S.creatures.get(id) : undefined;
    return c ? { id: myArt(c.species, c.rolls.aura), aura: !!c.rolls.aura } : null;
  })();
  for (const w of walkers) {
    const mine = w === me;
    const fit = mine ? mySprite() : trainerSprite(w.worn);
    const comp = mine ? myComp : w.comp ? { id: artOf(w.comp.species, w.comp.skin, w.comp.aura), aura: !!w.comp.aura } : null;
    items.push({ y: w.y, draw: () => drawTrainer(w, fit, t, now) });
    if (comp) items.push({ y: w.cy, draw: () => drawCompanion(w, comp, t, now) });
  }
  items.sort((a, b) => a.y - b.y);
  for (const it of items) it.draw();
  drawLayer(layers.front);
  drawMotes(t, now);

  // Names under everyone else, your own in gold; speech bubbles above.
  bubbles(now);
  for (const w of walkers) {
    const mine = w === me;
    // Until a new trainer picks a name, theirs is a stand-in: no label.
    if (!(mine && S.trainer.namePick)) label(mine ? S.trainer.name : `${w.tag ? `[${w.tag}] ` : ''}${w.name}`, sx(w.x), sy(w.y + 0.62), mine ? '#f0c45a' : '#f3e6c4');
    const em = w.emote && now - w.emote.at < EMOTE_MS ? w.emote : null;
    if (w.emote && !em) w.emote = undefined;
    if (em) drawEmote(em.id, w.x, w.y, now - em.at, t);
    if (w.bubble) {
      const rem = w.bubble.until - now;
      if (rem <= 0) w.bubble = undefined;
      else bubble(w.bubble.text, sx(w.x), sy(w.y - (em ? 2.9 : 2.1)), Math.min(1, rem / 400));
    }
  }

  // Arrived at a station that was clicked: use it.
  if (me && heading_to && Math.hypot(me.x - me.tx, me.y - me.ty) < 0.05) {
    const s = STATION[heading_to];
    if (Math.hypot(me.x - s.stand.x, me.y - s.stand.y) < 0.6) arrive(heading_to); else heading_to = null;
  }
  placePlates();
}

/** The app bar's Kinhall link: a hall's gable over a hearth flame. */
export const HALL_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 4l9 6.5"/><path d="M5 9.5V20h14V9.5"/><path d="M12 19.5c-1.9 0-3-1.2-3-2.8 0-1.8 1.7-2.6 2.2-4.7 1.6 1 3.8 2.8 3.8 4.9 0 1.5-1.1 2.6-3 2.6Z"/></svg>';

// ---------------- emote icons ----------------
// Placeholder pixel icons until the art thread draws ui-emote-<id> (then those are used).
const EMOTE_PIX: Record<string, { c: string; px: string[] }> = {
  wave: { c: '#f3d0a8', px: ['..#.#.#..', '..#.#.#.#', '..#.#.#.#', '#.#####.#', '#########', '.########', '..######.', '..#####..', '...###...'] },
  cheer: { c: '#f0c45a', px: ['....#....', '....#....', '...###...', '#########', '.#######.', '..#####..', '..##.##..', '.##...##.', '.#.....#.'] },
  heart: { c: '#e05a5a', px: ['.........', '.##...##.', '####.####', '#########', '#########', '.#######.', '..#####..', '...###...', '....#....'] },
  huh: { c: '#a9bfda', px: ['..#####..', '.##...##.', '.......##', '......##.', '....###..', '....##...', '.........', '....##...', '....##...'] },
  dance: { c: '#b46cff', px: ['....#####', '....#...#', '....#...#', '....#...#', '....#...#', '..###.###', '.########', '.###..###', '.........'] },
  bow: { c: '#dccbaa', px: ['.........', '..#...#..', '.###.###.', '#########', '.#######.', '..#####..', '...###...', '..#####..', '.##...##.'] },
  fireworks: { c: '#ffd468', px: ['#...#...#', '.#..#..#.', '..#.#.#..', '...###...', '#########', '...###...', '..#.#.#..', '.#..#..#.', '#...#...#'] },
  // Placeholder until the art thread draws ui-emote-ranked-salute: a raised sword.
  'ranked-salute': { c: '#d8dce6', px: ['....#....', '...###...', '...###...', '...###...', '...###...', '.#######.', '....#....', '...###...', '....#....'] },
};
const iconCache = new Map<string, string>();
/** An emote's icon as an <img>: the art drawn for `px` CSS pixels (and its 2x for sharp screens), else the placeholder. */
export function emoteImg(id: string, px = 24, cls = ''): string {
  const art = `ui-emote-${id}`;
  return has(art) ? uiIcon(art, '', cls, px) : `<img${cls ? ` class="${cls}"` : ''} src="${emoteIcon(id)}" alt="">`;
}
/** An emote's icon as an image URL: the art thread's still when it exists, else the pixel placeholder. */
export function emoteIcon(id: string): string {
  if (has(`ui-emote-${id}`)) return spriteUrl(`ui-emote-${id}`);
  let url = iconCache.get(id);
  if (url) return url;
  const p = EMOTE_PIX[id], c = document.createElement('canvas');
  c.width = c.height = 11;
  const x = c.getContext('2d')!;
  for (const [col, off] of [['#2a1d12', [[-1, 0], [1, 0], [0, -1], [0, 1]]], [p.c, [[0, 0]]]] as [string, number[][]][]) {
    x.fillStyle = col;
    for (const [dx, dy] of off) p.px.forEach((row, yy) => [...row].forEach((ch, xx) => { if (ch === '#') x.fillRect(xx + 1 + dx, yy + 1 + dy, 1, 1); }));
  }
  url = c.toDataURL();
  iconCache.set(id, url);
  return url;
}
const iconImgs = new Map<string, HTMLImageElement>();
/** The emote over a trainer's head: it pops in, bobs, and fades; Fireworks bursts gold sparks around them. */
function drawEmote(id: string, x: number, y: number, age: number, t: number) {
  // The art drawn nearest the size it lands at (16 map px, scaled by the camera), so it stays crisp.
  const want = [16, 32, 48].find((n) => n >= 16 * k * 0.8) ?? 48, key = `${id}@${want}`;
  let img = iconImgs.get(key);
  if (!img) { img = new Image(); img.src = has(`ui-emote-${id}`) && want !== 32 ? `art/ui-emote-${id}@${want}.png` : emoteIcon(id); iconImgs.set(key, img); }
  const pop = Math.min(1, age / 140), fade = Math.min(1, (EMOTE_MS - age) / 300);
  if (EMOTE[id]?.motion === 'burst') {
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2 + hash(i, 11), ph = ((age / 900) + hash(i, 12)) % 1;
      const r = (0.4 + ph * 1.4) * TILE;
      g.globalAlpha = (1 - ph) * fade;
      g.fillStyle = [R.gold[2], R.gold[3], R.flame[2]][i % 3];
      g.fillRect(Math.round(sx(x) + Math.cos(a) * r * k), Math.round(sy(y - 1.2) + Math.sin(a) * r * k * 0.8), Math.max(1, Math.round(k * 1.5)), Math.max(1, Math.round(k * 1.5)));
    }
    g.globalAlpha = 1;
  }
  if (!ready(img)) return;
  const size = 16 * k * (0.6 + 0.4 * pop), bob = Math.sin(t / 160) * k;
  const cx = sx(x), cy = sy(y - 2.35) + bob;
  g.globalAlpha = fade;
  g.fillStyle = 'rgba(244,232,204,.95)'; g.strokeStyle = '#7a5a2e'; g.lineWidth = Math.max(1, dpr);
  g.beginPath(); g.roundRect(cx - size / 2 - 3 * k, cy - size / 2 - 3 * k, size + 6 * k, size + 6 * k, 3 * k); g.fill(); g.stroke();
  g.imageSmoothingEnabled = false;
  g.drawImage(img, Math.round(cx - size / 2), Math.round(cy - size / 2), Math.round(size), Math.round(size));
  g.globalAlpha = 1;
}

/** A chat line as a bubble: the viewer's Chat filter applied, cut to 70 characters. */
function bubbleText(raw: string): string {
  const t = chatShown(raw);
  return t.length > 70 ? `${t.slice(0, 68)}…` : t;
}
